import { useState } from "react";
import {
  Controller,
  useForm,
  type SubmitHandler,
} from "react-hook-form";
import "./RsvpSection.scss";
import { TextInput } from "./Form/TextInput";
import { AttendanceSwitch } from "./Form/AttendanceSwitch";
import { CheckboxChip } from "./Form/CheckboxChip";
import { TextArea } from "./Form/TextArea";
import { SubmitButton } from "./Form/SubmitButton";
import { supabase } from "../../lib/supabase";
import { GuestInsert } from "../../types/database";

type RsvpFormValues = {
  lastName: string;
  firstName: string;
  phone: string;
  email: string;

  attending: boolean;

  lactoseIntolerant: boolean;
  glutenIntolerant: boolean;
  sugarFree: boolean;
  vegetarian: boolean;
  vegan: boolean;

  dietaryOther: string;
  favoriteSong: string;
  comment: string;

  acceptedPrivacy: boolean;
};

const defaultValues: RsvpFormValues = {
  lastName: "",
  firstName: "",
  phone: "",
  email: "",

  attending: true,

  lactoseIntolerant: false,
  glutenIntolerant: false,
  sugarFree: false,
  vegetarian: false,
  vegan: false,

  dietaryOther: "",
  favoriteSong: "",
  comment: "",

  acceptedPrivacy: false,
};

export const RsvpSection = () => {
  type SubmitStatus =
    | {
        type: "success";
        message: string;
      }
    | {
        type: "error";
        message: string;
      }
    | null;

  const [submitStatus, setSubmitStatus] =
    useState<SubmitStatus>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: {
      errors,
      isSubmitting
    },
  } = useForm<RsvpFormValues>({
    defaultValues,
    mode: "onBlur",
  });

  const isAttending = watch("attending");

  const onSubmit: SubmitHandler<RsvpFormValues> = async (
    formValues
  ) => {
    setSubmitStatus(null);

    const guest: GuestInsert = {
      first_name: formValues.firstName.trim(),
      last_name: formValues.lastName.trim(),
      email: formValues.email.trim().toLowerCase(),
      phone: formValues.phone.trim(),

      attending: formValues.attending,

      message: formValues.comment.trim() || null,

      song: formValues.attending
        ? formValues.favoriteSong.trim() || null
        : null,

      lactose_intolerant: formValues.attending
        ? formValues.lactoseIntolerant
        : false,

      gluten_intolerant: formValues.attending
        ? formValues.glutenIntolerant
        : false,

      sugar_free: formValues.attending
        ? formValues.sugarFree
        : false,

      vegetarian: formValues.attending
        ? formValues.vegetarian
        : false,

      vegan: formValues.attending
        ? formValues.vegan
        : false,

      other_dietary: formValues.attending
        ? formValues.dietaryOther.trim() || null
        : null,

      privacy_consent: formValues.acceptedPrivacy,
    };

    const { error } = await supabase
      .from("Guests")
      .insert(guest);

    if (error) {
      console.error("Supabase RSVP hiba:", error);

      setSubmitStatus({
        type: "error",
        message:
          "A visszajelzés elküldése nem sikerült. Kérjük, próbáld újra néhány perc múlva.",
      });

      return;
    }

    setSubmitStatus({
      type: "success",
      message:
        "Köszönjük! A visszajelzésedet sikeresen megkaptuk.",
    });

    reset(defaultValues);
  };

  return (
    <section id="rsvp" className="rsvp-section">
      <div className="rsvp-card">
        <header className="rsvp-header">
          <span className="rsvp-eyebrow">
            Részvételi visszajelzés
          </span>

          <h2>Visszajelzés</h2>

          <p>
            Kérünk augusztus 5-ig bezárólag töltsd ki az alábbi űrlapot,
             minden esetleg Veled érkező vendégre is, a gyermekekre is, hogy tudjuk,
            számíthatunk-e Rád életünk egyik legfontosabb napján
          </p>
        </header>

        <form
          className="rsvp-form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <section className="rsvp-form__section">
            <div className="rsvp-form__section-heading">
              <span className="rsvp-form__section-number">
                01
              </span>

              <div>
                <h3>Személyes adatok</h3>
                <p>
                  Add meg az elérhetőségeidet, hogy szükség
                  esetén fel tudjuk venni Veled a kapcsolatot.
                </p>
              </div>
            </div>

            <div className="rsvp-form__grid">
              <TextInput
                label="Vezetéknév"
                placeholder="Például Horváth"
                autoComplete="family-name"
                required
                error={errors.lastName?.message}
                {...register("lastName", {
                  required: "A vezetéknév megadása kötelező.",
                  minLength: {
                    value: 2,
                    message:
                      "A vezetéknév legalább 2 karakter legyen.",
                  },
                })}
              />

              <TextInput
                label="Keresztnév"
                placeholder="Például Rita"
                autoComplete="given-name"
                required
                error={errors.firstName?.message}
                {...register("firstName", {
                  required: "A keresztnév megadása kötelező.",
                  minLength: {
                    value: 2,
                    message:
                      "A keresztnév legalább 2 karakter legyen.",
                  },
                })}
              />

              <TextInput
                label="Telefonszám"
                type="tel"
                placeholder="+36 70 123 4567"
                autoComplete="tel"
                inputMode="tel"
                required
                error={errors.phone?.message}
                {...register("phone", {
                  required: "A telefonszám megadása kötelező.",
                  pattern: {
                    value: /^[+]?[\d\s/()-]{8,20}$/,
                    message:
                      "Kérjük, adj meg egy érvényes telefonszámot.",
                  },
                })}
              />

              <TextInput
                label="E-mail cím"
                type="email"
                placeholder="pelda@email.hu"
                autoComplete="email"
                inputMode="email"
                required
                error={errors.email?.message}
                {...register("email", {
                  required: "Az e-mail cím megadása kötelező.",
                  pattern: {
                    value:
                      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message:
                      "Kérjük, adj meg egy érvényes e-mail címet.",
                  },
                })}
              />
            </div>
          </section>

          <div className="rsvp-form__divider">
            <span />
            <i />
            <span />
          </div>

          <section className="rsvp-form__section">
            <div className="rsvp-form__section-heading">
              <span className="rsvp-form__section-number">
                02
              </span>

              <div>
                <h3>Részvétel</h3>
                <p>
                  Jelezd, hogy velünk tudsz-e ünnepelni.
                </p>
              </div>
            </div>

            <Controller
              name="attending"
              control={control}
              render={({ field }) => (
                <AttendanceSwitch
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  required
                />
              )}
            />
          </section>

          {isAttending && (
            <>
              <div className="rsvp-form__divider">
                <span />
                <i />
                <span />
              </div>

              <section className="rsvp-form__section">
                <div className="rsvp-form__section-heading">
                  <span className="rsvp-form__section-number">
                    03
                  </span>

                  <div>
                    <h3>Étkezési igények</h3>
                    <p>
                      Jelöld meg azokat a lehetőségeket,
                      amelyek Rád vonatkoznak.
                    </p>
                  </div>
                </div>

                <div className="rsvp-form__chips">
                  <CheckboxChip
                    label="Laktózérzékeny"
                    {...register("lactoseIntolerant")}
                  />

                  <CheckboxChip
                    label="Gluténérzékeny"
                    {...register("glutenIntolerant")}
                  />

                  <CheckboxChip
                    label="Cukormentes"
                    {...register("sugarFree")}
                  />

                  <CheckboxChip
                    label="Vegetáriánus"
                    {...register("vegetarian")}
                  />

                  <CheckboxChip
                    label="Vegán"
                    {...register("vegan")}
                  />
                </div>

                <div className="rsvp-form__full-width">
                  <TextArea
                    label="Egyéb ételérzékenység vagy étkezési igény"
                    placeholder="Írd meg, ha van olyan kérésed, amely nem szerepel a felsorolásban."
                    rows={4}
                    hint="Ezt a mezőt csak akkor töltsd ki, ha szükséges."
                    {...register("dietaryOther")}
                  />
                </div>
              </section>

              <div className="rsvp-form__divider">
                <span />
                <i />
                <span />
              </div>

              <section className="rsvp-form__section">
                <div className="rsvp-form__section-heading">
                  <span className="rsvp-form__section-number">
                    04
                  </span>

                  <div>
                    <h3>Zene és üzenet</h3>
                    <p>
                      Segíts nekünk még személyesebbé tenni az
                      estét.
                    </p>
                  </div>
                </div>

                <div className="rsvp-form__stack">
                  <TextInput
                    label="Kedvenc dalod"
                    placeholder="Előadó – Dal címe"
                    hint="Írd meg, melyik dalra táncolnál mindenképpen az esküvőnkön."
                    {...register("favoriteSong")}
                  />

                  <TextArea
                    label="Megjegyzés"
                    placeholder="Ha szeretnél még valamit üzenni nekünk, itt megteheted."
                    rows={5}
                    {...register("comment")}
                  />
                </div>
              </section>
            </>
          )}

          {!isAttending && (
            <>
              <div className="rsvp-form__divider">
                <span />
                <i />
                <span />
              </div>

              <section className="rsvp-form__section">
                <div className="rsvp-form__section-heading">
                  <span className="rsvp-form__section-number">
                    03
                  </span>

                  <div>
                    <h3>Üzenet</h3>
                    <p>
                      Sajnáljuk, hogy nem tudsz velünk ünnepelni,
                      de szívesen fogadjuk az üzenetedet.
                    </p>
                  </div>
                </div>

                <TextArea
                  label="Megjegyzés"
                  placeholder="Írj nekünk néhány sort..."
                  rows={5}
                  {...register("comment")}
                />
              </section>
            </>
          )}

          <div className="rsvp-form__divider">
            <span />
            <i />
            <span />
          </div>

          <section className="rsvp-form__section">
            <div className="rsvp-form__section-heading">
              <span className="rsvp-form__section-number">
                {isAttending ? "05" : "04"}
              </span>

              <div>
                <h3>Adatkezelés</h3>
                <p>
                  A megadott adatokat kizárólag az esküvővel
                  kapcsolatos egyeztetéshez használjuk fel.
                </p>
              </div>
            </div>

            <CheckboxChip
              className="rsvp-form__privacy"
              label="Hozzájárulok személyes adataim kezeléséhez és továbbításához kizárólag kapcsolatfelvétel és az esküvő megszervezése céljából."
              required
              error={errors.acceptedPrivacy?.message}
              {...register("acceptedPrivacy", {
                validate: (value) =>
                  value ||
                  "A visszajelzés elküldéséhez szükséges az adatkezelési hozzájárulás.",
              })}
            />
          </section>

          <div className="rsvp-form__actions">
            <SubmitButton
              loading={isSubmitting}
              disabled={isSubmitting}
              icon={
                <span className="material-symbols-rounded">
                  
                </span>
              }
            >
              Visszajelzés elküldése
            </SubmitButton>

            {submitStatus && (
              <p
                className={[
                  "rsvp-form__submit-message",
                  submitStatus.type === "success"
                    ? "rsvp-form__submit-message--success"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                role="status"
              >
                {submitStatus.message}
              </p>
            )}
          </div>
        </form>

        <footer className="rsvp-signature">
          <div className="rsvp-signature__divider">
            <span />
            <i />
            <span />
          </div>

          <div className="rsvp-signature__monogram">
            <span>V</span>
            <span className="rsvp-signature__ampersand">
              &amp;
            </span>
            <span>R</span>
          </div>
        </footer>
      </div>
    </section>
  );
};